import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import CustomNavbar from '../components/Navbar';
import Footer from '../components/Footer';
import { fetchMediaGalleries, fetchMediaGalleryBySlug } from '../api/mediaGalleries';
import Seo from '../components/Seo';
import NotFoundPage from './NotFoundPage';
import { PAGE_SEO } from '../seo/pages';

function MediaGalleryPage() {
  const { slug } = useParams();
  const [gallery, setGallery] = useState(null);
  const [otherGalleries, setOtherGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const viewerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setGallery(null);
    setIndex(0);
    Promise.all([
      fetchMediaGalleryBySlug(slug),
      fetchMediaGalleries().catch((error) => {
        console.error('Error fetching media galleries:', error);
        return [];
      }),
    ])
      .then(([item, list]) => {
        if (cancelled) return;
        setGallery(item);
        setOtherGalleries(list.filter((entry) => entry.slug !== slug));
      })
      .catch((error) => {
        console.error('Error fetching media gallery:', error);
        if (!cancelled) {
          setGallery(null);
          setOtherGalleries([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const imageCount = gallery?.images?.length ?? 0;
  const current = gallery?.images?.[index];

  const goTo = useCallback(
    (nextIndex) => {
      if (!imageCount) return;
      const wrapped = ((nextIndex % imageCount) + imageCount) % imageCount;
      setIndex(wrapped);
    },
    [imageCount]
  );

  const goPrev = useCallback(() => goTo(index - 1), [goTo, index]);
  const goNext = useCallback(() => goTo(index + 1), [goTo, index]);

  useEffect(() => {
    setIndex(0);
  }, [slug]);

  useEffect(() => {
    if (loading || !gallery || imageCount <= 1) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loading, gallery, imageCount, goPrev, goNext]);

  const onTouchStart = (event) => {
    touchStartX.current = event.changedTouches[0]?.clientX ?? null;
  };

  const onTouchEnd = (event) => {
    if (touchStartX.current == null || imageCount <= 1) return;
    const endX = event.changedTouches[0]?.clientX ?? touchStartX.current;
    const delta = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(delta) < 40) return;
    if (delta > 0) goPrev();
    else goNext();
  };

  if (loading) {
    return (
      <div className="media-gallery-page">
        <CustomNavbar />
        <section className="media-gallery-page__intro py-5">
          <Container>
            <p className="media-centre-empty">Loading…</p>
          </Container>
        </section>
        <Footer />
      </div>
    );
  }

  if (!gallery) {
    return <NotFoundPage />;
  }

  const firstSection = gallery.sections?.[0];
  const extraSections = gallery.sections?.slice(1) || [];

  return (
    <div className="media-gallery-page">
      <Seo
        title={`${gallery.title} | Q-Gold Resources`}
        description={gallery.intro || PAGE_SEO.mediaCentre.description}
        path={`/media-centre/${gallery.slug || slug}`}
      />
      <CustomNavbar />

      <section className="media-gallery-page__intro py-5">
        <Container>
          <Link to="/media-centre" className="media-gallery-page__back">
            ← Media Centre
          </Link>
          <time className="media-gallery-page__date" dateTime={gallery.date}>
            {gallery.dateLabel}
          </time>
          <h1 className="media-gallery-page__title">{gallery.title}</h1>
          {gallery.intro ? (
            <p className="media-gallery-page__description">{gallery.intro}</p>
          ) : null}
        </Container>
      </section>

      {firstSection && (firstSection.heading || firstSection.body) && (
        <section className="media-gallery-article" aria-labelledby="gallery-section-0">
          <Container>
            <h2 id="gallery-section-0" className="media-gallery-article__title">
              {firstSection.heading}
            </h2>
            {firstSection.body ? (
              <div
                className="media-gallery-article__body"
                dangerouslySetInnerHTML={{ __html: firstSection.body }}
              />
            ) : null}
          </Container>
        </section>
      )}

      <section className="media-gallery-viewer-section" aria-label="Photo gallery">
        <Container>
          <div
            className="media-gallery-viewer"
            ref={viewerRef}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="media-gallery-viewer__stage">
              {imageCount > 1 && (
                <button
                  type="button"
                  className="media-gallery-viewer__nav media-gallery-viewer__nav--prev"
                  onClick={goPrev}
                  aria-label="Previous image"
                >
                  ‹
                </button>
              )}

              <figure className="media-gallery-viewer__figure">
                {current ? (
                  <img
                    key={current.src + index}
                    src={current.src}
                    alt={current.alt || gallery.title}
                    className="media-gallery-viewer__image"
                  />
                ) : (
                  <div className="media-gallery-viewer__missing" role="img" aria-label="Image unavailable">
                    Image unavailable
                  </div>
                )}
                <figcaption className="media-gallery-viewer__caption">
                  <div className="media-gallery-viewer__counter" aria-live="polite">
                    {imageCount > 0 ? `${index + 1} / ${imageCount}` : '0 / 0'}
                  </div>
                  {current?.caption && (
                    <p className="media-gallery-viewer__caption-text">{current.caption}</p>
                  )}
                  {current?.description && (
                    <p className="media-gallery-viewer__caption-desc">{current.description}</p>
                  )}
                </figcaption>
              </figure>

              {imageCount > 1 && (
                <button
                  type="button"
                  className="media-gallery-viewer__nav media-gallery-viewer__nav--next"
                  onClick={goNext}
                  aria-label="Next image"
                >
                  ›
                </button>
              )}
            </div>

            {imageCount > 1 && (
              <ul className="media-gallery-thumbs" aria-label="Gallery thumbnails">
                {gallery.images.map((image, i) => (
                  <li key={`${image.src}-${i}`}>
                    <button
                      type="button"
                      className={`media-gallery-thumbs__btn${i === index ? ' is-active' : ''}`}
                      onClick={() => setIndex(i)}
                      aria-label={`Show image ${i + 1}${image.caption ? `: ${image.caption}` : ''}`}
                      aria-current={i === index ? 'true' : undefined}
                    >
                      <img src={image.src} alt="" loading="lazy" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Container>
      </section>

      {extraSections.map((section, sectionIndex) => {
        if (!section.heading && !section.body) return null;
        return (
          <section
            key={section.heading || `gallery-section-${sectionIndex + 1}`}
            className="media-gallery-article"
            aria-labelledby={`gallery-section-${sectionIndex + 1}`}
          >
            <Container>
              <h2 id={`gallery-section-${sectionIndex + 1}`} className="media-gallery-article__title">
                {section.heading}
              </h2>
              {section.body ? (
                <div
                  className="media-gallery-article__body"
                  dangerouslySetInnerHTML={{ __html: section.body }}
                />
              ) : null}
            </Container>
          </section>
        );
      })}

      {otherGalleries.length > 0 && (
        <section className="media-gallery-more-section pb-5">
          <Container>
            <nav className="media-gallery-more" aria-label="More galleries">
              <h2 className="media-gallery-more__title">More Galleries</h2>
              <ul className="media-gallery-more__list">
                {otherGalleries.map((item) => (
                  <li key={item.slug}>
                    <Link to={`/media-centre/${item.slug}`} className="media-gallery-more__link">
                      {item.title}
                      <span aria-hidden="true"> ➔</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </Container>
        </section>
      )}

      <Footer />
    </div>
  );
}

export default MediaGalleryPage;
